import _get from 'lodash/get'
import { print } from 'graphql'
import { post as apiPost, TYPE_GRAPHQL, getPublicGraphqlUrl } from '../../api-client/index'
import { createAuthForRequest } from '../auth/index'
import { CONTENT_IMAGE_LIST } from '../content/index'
import { GET_PM_SCREEN_PERSONALIZED_SSR } from './query'
import { PM_SCREEN_TYPE_MEMBER_HOME } from './types'

export const PM_SCREEN_HYDRATE_KEY = 'pmScreenHydrate'

/**
 * PM rows with full `app` data fetched on SSR and client initial `getPmScreen`.
 * Keep in sync with `watchers.js`.
 */
export const MEMBER_HOME_SECTIONS_LIMIT = 4

/**
 * SSR fetch for Member Home initial PM rows via the content GraphQL gateway
 * (`servers.graphql`), not Brooklyn GraphQL or REST.
 * @see lib/components/MemberHome/ARCHITECTURE.md
 */
export async function fetchMemberHomePmScreen (options: {
  language: string
  auth: unknown
  sectionsLimit?: number
}) {
  const {
    language,
    auth,
    sectionsLimit = MEMBER_HOME_SECTIONS_LIMIT,
  } = options
  const screenType = PM_SCREEN_TYPE_MEMBER_HOME
  const variables = {
    screenType,
    language: language ? [language] : undefined,
    sectionsLimit,
    contentLanguage: language ? [language, '_primary'] : undefined,
    imageType: CONTENT_IMAGE_LIST,
  }
  try {
    const res = await apiPost(
      getPublicGraphqlUrl(),
      { query: print(GET_PM_SCREEN_PERSONALIZED_SSR), variables },
      {
        auth: createAuthForRequest({ auth: auth as never, includeJwt: true }),
        timeout: 10000,
        maxAttempts: 1,
      } as never,
      TYPE_GRAPHQL,
    )
    // Partial GraphQL errors must not wipe a usable pmScreen.
    const pmScreenData = _get(res, ['body', 'data', 'pmScreenPersonalized', 'pmScreen'])
    if (!pmScreenData) {
      return null
    }
    const pmSections = _get(pmScreenData, 'pmSections', [])
    const sectionIds = pmSections.map((section: { id: string }) => section.id)
    const sectionsWithDetails = _get(pmScreenData, 'pmSectionsWithDetails', [])
    const allPmLists: unknown[] = []
    sectionsWithDetails.forEach((section: { pmList?: unknown[] }) => {
      if (section.pmList && section.pmList.length > 0) {
        allPmLists.push(...section.pmList)
      }
    })
    const sectionsToStore = sectionsWithDetails
      .filter((section: { app?: unknown }) => section.app)
      .map((section: { id: string; app: unknown; pmList?: unknown }) => ({
        id: section.id,
        app: section.app,
        pmList: section.pmList,
      }))

    return {
      screenType,
      language,
      sectionsLimit,
      screenData: {
        ...pmScreenData,
        sectionIds,
      },
      sections: sectionsToStore,
      lists: allPmLists,
    }
  } catch {
    return null
  }
}
